import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Briefcase, Circle, Copy, MoreHorizontal, Plus, Search, Users } from 'lucide-react'
import {
  getMyJobs,
  deleteMyJob,
  setMyJobStatus,
  duplicateMyJob,
  getJobCategories,
  WORK_TYPE_LABELS,
  EXP_LABELS,
  type ApiJobListItem,
  type ApiJobCategory,
} from '@/services/jobService'

// สถานะจริงจาก backend มี 5 ค่า (DRAFT/ACTIVE/PAUSED/CLOSED/EXPIRED)
// - PAUSED = ถูกระงับโดยทีมงาน (บริษัทแก้ไข/เปิด/ทำสำเนาเองไม่ได้) — แสดงแยก แต่นับอยู่ในกลุ่ม "ปิดรับสมัคร"
// - CLOSED/EXPIRED = ปิดรับสมัคร (บริษัทเปิดอีกครั้งได้)
type ViewStatus = 'ACTIVE' | 'DRAFT' | 'CLOSED' | 'SUSPENDED'
type FilterKey = 'ALL' | 'ACTIVE' | 'DRAFT' | 'CLOSED'
const toViewStatus = (status: string): ViewStatus =>
  status === 'ACTIVE' ? 'ACTIVE' : status === 'DRAFT' ? 'DRAFT' : status === 'PAUSED' ? 'SUSPENDED' : 'CLOSED'
const toFilterGroup = (status: string): Exclude<FilterKey, 'ALL'> => {
  const st = toViewStatus(status)
  return st === 'SUSPENDED' ? 'CLOSED' : st
}

const STATUS_META: Record<ViewStatus, { label: string; dot: string; pill: string }> = {
  ACTIVE: { label: 'กำลังเปิดรับ', dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700' },
  DRAFT: { label: 'แบบร่าง', dot: 'bg-[#69738A]', pill: 'bg-[#F1F0F7] text-[#69738A]' },
  CLOSED: { label: 'ปิดรับสมัคร', dot: 'bg-rose-500', pill: 'bg-rose-50 text-rose-700' },
  SUSPENDED: { label: 'ถูกระงับโดยทีมงาน', dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700' },
}
const FILTER_TABS: { key: FilterKey; label: string }[] = [
  { key: 'ALL', label: 'ทั้งหมด' },
  { key: 'ACTIVE', label: 'กำลังเปิดรับ' },
  { key: 'DRAFT', label: 'แบบร่าง' },
  { key: 'CLOSED', label: 'ปิดรับสมัคร' },
]

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'

// ทุกปุ่มยิง API จริง (2026-09-28): แก้ไข → /post-job?edit=<id> (PUT /api/my-jobs/:id),
// เผยแพร่ร่าง/ปิดรับสมัคร/เปิดอีกครั้ง → PATCH /api/my-jobs/:id/status, ทำสำเนา → POST /api/my-jobs/:id/duplicate,
// ลบ → DELETE /api/my-jobs/:id — ไม่ต้องรอแอดมินอนุมัติ มีผลทันที

const PAGE_SIZE = 5

export default function JobPostingsTab() {
  const navigate = useNavigate()
  const location = useLocation()
  const [jobs, setJobs] = useState<ApiJobListItem[]>([])
  const [categories, setCategories] = useState<ApiJobCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<FilterKey>('ALL')
  const [sort, setSort] = useState<'latest' | 'applicants'>('latest')
  const [page, setPage] = useState(1)
  const [openMenu, setOpenMenu] = useState<{ id: number; rect: DOMRect } | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [toast, setToast] = useState('')

  // โหลดรายการงาน — ถ้ากลับมาจากหน้าแก้ไข (/post-job?edit=) จะมีข้อความแจ้งผลแนบมาใน router state
  // การอ่าน/ล้าง location.state อยู่ใน .then() เดียวกับการโหลดข้อมูล (ไม่ setState ตรงๆ ในตัว effect)
  useEffect(() => {
    const savedMessage = (location.state as { savedMessage?: string } | null)?.savedMessage
    Promise.all([getMyJobs(), getJobCategories()])
      .then(([jobList, cats]) => {
        if (savedMessage) {
          setToast(savedMessage)
          navigate(location.pathname + location.search, { replace: true, state: null })
        }
        setJobs(jobList)
        setCategories(cats)
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'โหลดประกาศงานไม่สำเร็จ'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 2500)
    return () => clearTimeout(t)
  }, [toast])

  const categoryName = (id: number) => categories.find((c) => c.job_category_id === id)?.name

  const summary = useMemo(
    () => ({
      total: jobs.length,
      active: jobs.filter((j) => toFilterGroup(j.status) === 'ACTIVE').length,
      draft: jobs.filter((j) => toFilterGroup(j.status) === 'DRAFT').length,
      closed: jobs.filter((j) => toFilterGroup(j.status) === 'CLOSED').length,
    }),
    [jobs]
  )

  const filtered = useMemo(() => {
    let list = jobs
    if (filter !== 'ALL') list = list.filter((j) => toFilterGroup(j.status) === filter)
    const q = search.trim().toLowerCase()
    if (q) list = list.filter((j) => j.title.toLowerCase().includes(q))
    list = [...list]
    if (sort === 'applicants') list.sort((a, b) => b.application_count - a.application_count)
    else list.sort((a, b) => new Date(b.published_at || b.created_date).getTime() - new Date(a.published_at || a.created_date).getTime())
    return list
  }, [jobs, filter, search, sort])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // เปลี่ยนตัวกรอง/ค้นหา/เรียงลำดับแล้วกลับไปหน้า 1 เสมอ — ตั้งค่าตรงจุดที่ผู้ใช้กดแทนการใช้ effect
  const setFilterAndResetPage = (v: typeof filter) => { setFilter(v); setPage(1) }
  const setSearchAndResetPage = (v: string) => { setSearch(v); setPage(1) }
  const setSortAndResetPage = (v: typeof sort) => { setSort(v); setPage(1) }

  // เผยแพร่ร่าง / ปิดรับสมัคร / เปิดอีกครั้ง — อัปเดตแถวด้วยข้อมูลที่ server ส่งกลับ
  const changeStatus = (row: ApiJobListItem, status: 'ACTIVE' | 'CLOSED', doneMessage: string) => {
    setOpenMenu(null)
    setBusyId(row.job_id)
    setMyJobStatus(row.job_id, status)
      .then((updated) => {
        setJobs((prev) => prev.map((j) => (j.job_id === row.job_id ? { ...j, ...updated } : j)))
        setToast(doneMessage)
      })
      .catch((err) => setToast(err instanceof Error ? err.message : 'เปลี่ยนสถานะประกาศไม่สำเร็จ'))
      .finally(() => setBusyId(null))
  }

  const duplicateJob = (row: ApiJobListItem) => {
    setOpenMenu(null)
    setBusyId(row.job_id)
    duplicateMyJob(row.job_id)
      .then((copy) => {
        setJobs((prev) => [copy, ...prev])
        setToast(`ทำสำเนาเป็นแบบร่าง "${copy.title}" แล้ว`)
      })
      .catch((err) => setToast(err instanceof Error ? err.message : 'ทำสำเนาประกาศไม่สำเร็จ'))
      .finally(() => setBusyId(null))
  }

  const deleteJob = (row: ApiJobListItem) => {
    if (!window.confirm(`ลบประกาศงาน "${row.title}"? การกระทำนี้ย้อนกลับไม่ได้`)) return
    setOpenMenu(null)

    const prevJobs = jobs
    setJobs((prev) => prev.filter((j) => j.job_id !== row.job_id))
    deleteMyJob(row.job_id)
      .then(() => setToast(`ลบ "${row.title}" แล้ว`))
      .catch((err) => {
        setJobs(prevJobs) // ลบไม่สำเร็จ — เอากลับมาแสดงเหมือนเดิม
        setToast(err instanceof Error ? err.message : 'ลบประกาศงานไม่สำเร็จ')
      })
  }

  return (
    <div>
      {toast && (
        <div className="mb-5 rounded-xl border border-accent/20 bg-accent/5 px-4 py-2.5 text-sm text-accent">{toast}</div>
      )}
      {error && <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">{error}</div>}

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard label="ประกาศทั้งหมด" value={summary.total} icon={Briefcase} active={filter === 'ALL'} onClick={() => setFilterAndResetPage('ALL')} />
        <SummaryCard label="กำลังเปิดรับ" value={summary.active} dotClass="bg-emerald-500" active={filter === 'ACTIVE'} onClick={() => setFilterAndResetPage('ACTIVE')} />
        <SummaryCard label="แบบร่าง" value={summary.draft} dotClass="bg-[#69738A]" active={filter === 'DRAFT'} onClick={() => setFilterAndResetPage('DRAFT')} />
        <SummaryCard label="ปิดรับสมัคร" value={summary.closed} dotClass="bg-rose-500" active={filter === 'CLOSED'} onClick={() => setFilterAndResetPage('CLOSED')} />
      </div>

      {/* หัวข้อ/คำอธิบายย้ายไปแสดงรวมกับหัวหน้าของ AccountShell แล้ว (TAB_SUBTITLES ใน Account/index.tsx) — เหลือแค่ปุ่มสร้างประกาศ (สลับมาไว้ใต้การ์ดสรุปตามที่ขอ) */}
      <div className="mb-6 flex justify-end">
        <Link
          to="/post-job"
          className="inline-flex items-center gap-1.5 rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-accent2"
        >
          <Plus className="h-4 w-4" /> สร้างประกาศงาน
        </Link>
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#69738A]" />
          <input
            value={search}
            onChange={(e) => setSearchAndResetPage(e.target.value)}
            placeholder="ค้นหาชื่อตำแหน่งงาน..."
            className="w-full rounded-xl border border-[#E7E5FF] bg-white py-2.5 pl-10 pr-3.5 text-sm text-[#19172A] outline-none transition focus:border-accent"
          />
        </div>
        <div className="flex flex-wrap gap-1.5 rounded-xl bg-[#F5F3FC] p-1">
          {FILTER_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilterAndResetPage(t.key)}
              className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition ${
                filter === t.key ? 'bg-accent text-white shadow-sm' : 'text-[#69738A] hover:text-[#19172A]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={(e) => setSortAndResetPage(e.target.value as typeof sort)}
          className="rounded-xl border border-[#E7E5FF] bg-white px-3 py-2.5 text-sm text-[#19172A] outline-none"
        >
          <option value="latest">ล่าสุด</option>
          <option value="applicants">ผู้สมัครมากสุด</option>
        </select>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-2xl border border-[#E7E5FF] bg-white">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#69738A]">กำลังโหลด...</div>
        ) : pageItems.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#69738A]">ไม่พบประกาศงาน</div>
        ) : (
          <ul className="divide-y divide-[#F0EEFB]">
            {pageItems.map((job) => {
              const st = toViewStatus(job.status)
              const meta = STATUS_META[st]
              const cat = categoryName(job.category_id)
              return (
                <li key={job.job_id} className={`flex flex-wrap items-center gap-4 px-5 py-4 transition hover:bg-[#FAFAFF] ${busyId === job.job_id ? 'pointer-events-none opacity-60' : ''}`}>
                  <div className="min-w-0 flex-1" style={{ flexBasis: 260 }}>
                    <p className="truncate text-sm font-semibold text-[#19172A]">{job.title}</p>
                    <p className="mt-0.5 truncate text-xs text-[#69738A]">
                      {job.location} • {job.is_remote ? 'Remote' : WORK_TYPE_LABELS[job.work_type] || job.work_type} • {EXP_LABELS[job.experience_level] || job.experience_level}
                    </p>
                    {cat && (
                      <span className="mt-1.5 inline-block rounded-md bg-[#F1F0F7] px-2 py-0.5 text-[11px] text-[#69738A]">{cat}</span>
                    )}
                  </div>

                  <div className="w-32 shrink-0">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${meta.pill}`}>
                      <Circle className={`h-1.5 w-1.5 ${meta.dot} fill-current`} />
                      {meta.label}
                    </span>
                    <p className="mt-1.5 text-[11px] text-[#69738A]">
                      {st === 'DRAFT' ? 'สร้าง ' : st === 'ACTIVE' ? 'เผยแพร่ ' : 'เผยแพร่เมื่อ '}
                      {formatDate(job.published_at || job.created_date)}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-5 text-xs text-[#69738A]">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      <div>
                        <p className="leading-none text-[#19172A]">{job.application_count} คน</p>
                        <p className="mt-0.5 leading-none">ผู้สมัคร</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {!(st === 'DRAFT' && job.application_count === 0) && (
                      <button
                        onClick={() => navigate(`/account?tab=job-postings&applicants=${job.job_id}`, { state: { job } })}
                        className="rounded-lg border border-[#E7E5FF] px-3 py-1.5 text-xs font-medium text-[#19172A] transition hover:border-accent hover:text-accent"
                      >
                        ดูผู้สมัคร ({job.application_count})
                      </button>
                    )}
                    {st === 'DRAFT' && (
                      <button
                        onClick={() => changeStatus(job, 'ACTIVE', `เผยแพร่ "${job.title}" แล้ว`)}
                        className="rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-white transition hover:bg-accent2"
                      >
                        เผยแพร่
                      </button>
                    )}
                    {/* ประกาศที่ถูกระงับโดยทีมงาน แก้ไข/เปิด/ทำสำเนาเองไม่ได้ (backend ปฏิเสธด้วย) — เหลือแค่ลบ */}
                    {st !== 'SUSPENDED' && (
                      <button
                        onClick={() => navigate(`/post-job?edit=${job.job_id}`)}
                        className="rounded-lg border border-[#E7E5FF] px-3 py-1.5 text-xs font-medium text-[#19172A] transition hover:border-accent hover:text-accent"
                      >
                        แก้ไข
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        const opening = openMenu?.id !== job.job_id
                        setOpenMenu(opening ? { id: job.job_id, rect: e.currentTarget.getBoundingClientRect() } : null)
                      }}
                      className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#E7E5FF] text-[#69738A] transition hover:border-accent hover:text-accent"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                    {openMenu?.id === job.job_id && (
                      <RowMenu anchorRect={openMenu.rect} onClose={() => setOpenMenu(null)}>
                        {/* หน้าประกาศสาธารณะแสดงเฉพาะประกาศที่เปิดรับอยู่ */}
                        {st === 'ACTIVE' && (
                          <Link
                            to={`/jobs?job=${job.job_id}`}
                            onClick={() => setOpenMenu(null)}
                            className="block px-3.5 py-2 text-sm text-[#19172A] hover:bg-[#F5F3FC]"
                          >
                            ดูประกาศ
                          </Link>
                        )}
                        {st !== 'SUSPENDED' && (
                          <button
                            onClick={() => duplicateJob(job)}
                            className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm text-[#19172A] hover:bg-[#F5F3FC]"
                          >
                            <Copy className="h-3.5 w-3.5" /> ทำสำเนา
                          </button>
                        )}
                        {st === 'ACTIVE' && (
                          <button
                            onClick={() => changeStatus(job, 'CLOSED', `ปิดรับสมัคร "${job.title}" แล้ว`)}
                            className="block w-full px-3.5 py-2 text-left text-sm text-[#19172A] hover:bg-[#F5F3FC]"
                          >
                            ปิดรับสมัคร
                          </button>
                        )}
                        {st === 'CLOSED' && (
                          <button
                            onClick={() => changeStatus(job, 'ACTIVE', `เปิดรับสมัคร "${job.title}" อีกครั้งแล้ว`)}
                            className="block w-full px-3.5 py-2 text-left text-sm text-[#19172A] hover:bg-[#F5F3FC]"
                          >
                            เปิดรับสมัครอีกครั้ง
                          </button>
                        )}
                        <button
                          onClick={() => deleteJob(job)}
                          className="block w-full px-3.5 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                        >
                          ลบประกาศ
                        </button>
                      </RowMenu>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {!loading && filtered.length > 0 && (
        <div className="mt-4 flex items-center justify-between text-sm text-[#69738A]">
          <p>
            แสดง {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, filtered.length)} จาก {filtered.length} รายการ
          </p>
          <div className="flex items-center gap-1.5">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E7E5FF] disabled:opacity-40"
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium ${
                  n === page ? 'bg-accent text-white' : 'border border-[#E7E5FF] text-[#69738A] hover:text-[#19172A]'
                }`}
              >
                {n}
              </button>
            ))}
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E7E5FF] disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      )}

    </div>
  )
}

// ── การ์ดสรุป ────────────────────────────────────────────────────────────────
function SummaryCard({
  label,
  value,
  icon: Icon,
  dotClass,
  active,
  onClick,
}: {
  label: string
  value: number
  icon?: typeof Briefcase
  dotClass?: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 rounded-xl border bg-white p-4 text-left transition ${
        active ? 'border-accent shadow-[0_0_0_1px_rgba(108,99,255,0.35)]' : 'border-[#E7E5FF] hover:border-accent/40'
      }`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F5F3FC]">
        {Icon ? <Icon className="h-4 w-4 text-accent" /> : <span className={`h-2.5 w-2.5 rounded-full ${dotClass}`} />}
      </div>
      <div>
        <p className="text-xs text-[#69738A]">{label}</p>
        <p className="text-lg font-semibold text-[#19172A]">{value}</p>
      </div>
    </button>
  )
}

// ── เมนู "•••" ของแต่ละแถว ─────────────────────────────────────────────────────
// วาดผ่าน portal ไปที่ document.body และคำนวณตำแหน่งเป็น position:fixed จาก anchorRect ของปุ่มที่กด
// (ไม่ใช้ absolute ธรรมดาซ้อนในแถว) เพราะรายการอยู่ในกล่อง overflow-hidden (มุมโค้ง) ถ้าเมนูเป็น absolute
// ภายในนั้น มันจะถูกตัดขอบไปด้วยเวลาแถวอยู่ใกล้ขอบล่างของกล่อง — เมนูของแถวท้ายๆ เลย "ตกขอบ"/โดนตัด
export function RowMenu({ anchorRect, onClose, children }: { anchorRect: DOMRect; onClose: () => void; children: React.ReactNode }) {
  const MENU_WIDTH = 176
  const left = Math.min(anchorRect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)
  const top = Math.min(anchorRect.bottom + 6, window.innerHeight - 8)
  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 overflow-hidden rounded-xl border border-[#E7E5FF] bg-white py-1 shadow-lg"
        style={{ left: Math.max(left, 8), top, width: MENU_WIDTH }}
      >
        {children}
      </div>
    </>,
    document.body
  )
}
